import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { BenchmarkTriple } from "@/components/data/BenchmarkTriple";
import { AnalogueMeter } from "@/components/data/AnalogueMeter";
import { BasisBadge } from "@/components/trust/BasisBadge";
import { ConfidenceIndicator } from "@/components/trust/ConfidenceIndicator";

const UTILITY_LABELS = {
  steam: "Steam", cooling_water: "Cooling Water", electricity: "Electricity",
  compressed_air: "Compressed Air", instrument_air: "Instrument Air", nitrogen: "Nitrogen", wastewater: "Waste Water",
};

export default function Energy() {
  const { data, error } = usePolling(() => api.get("/intelligence/energy").then((r) => r.data), 5000);

  if (error) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="energy-error">Could not load energy layer.</div>;
  if (!data) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="energy-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="energy-page">
      <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Energy & Resource</h1>
      <div className="text-xs text-[var(--oim-text-dim)]">Shared utility consumption across the Benzene, Toluene and Valorization streams</div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Object.entries(data.utility_breakdown || {}).map(([key, u]) => (
            <div key={key} className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid={`utility-card-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] text-[var(--oim-text-dim)]">{u.source}</div>
                {u.scope ? (
                  <span className="text-[8px] font-heading uppercase px-1.5 py-0.5 rounded-full border border-[var(--oim-border)] text-[var(--oim-text-secondary)]" data-testid={`utility-scope-${key}`} title={u.scope_note}>SCOPE {u.scope}</span>
                ) : null}
              </div>
              <BenchmarkTriple label={UTILITY_LABELS[key]} unit={u.unit} actual={u.actual} shouldbe={u.shouldbe} golden={u.golden}
                basis={u.basis} confidence={u.confidence} testId={`benchmark-utility-${key}`} />
              <div className="text-[10px] font-mono-data text-[var(--oim-text-dim)] mt-2 whitespace-nowrap">{u.hourly_value} {u.hourly_unit}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border p-4 flex flex-col items-center justify-center gap-2" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
          {data.chiller_cop && (
            <>
              <AnalogueMeter
                value={data.chiller_cop.actual} min={0} max={6}
                bands={[{ from: 0, to: 3, color: "#FF3B30" }, { from: 3, to: 4.2, color: "#FFB300" }, { from: 4.2, to: 6, color: "#00C853" }]}
                label="Chiller CH-01 COP" unit="" sublabel={`should-be ${data.chiller_cop.shouldbe}`} testId="analogue-meter-chiller-cop"
              />
              <div className="flex items-center gap-2">
                <BasisBadge basis={data.chiller_cop.basis} />
                <ConfidenceIndicator level={data.chiller_cop.confidence} limitedBy={data.chiller_cop.limited_by} />
              </div>
            </>
          )}
          <div className="w-full pt-3 mt-2 border-t border-[var(--oim-border)] space-y-1.5">
            <div className="text-[9px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading">H2 Consumption</div>
            {(data.h2_consumption || []).map((h) => (
              <div key={h.utility} className="flex justify-between font-mono-data text-xs">
                <span className="text-[var(--oim-text-muted)]">{h.utility}</span>
                <span className="text-[var(--oim-text)]">{h.value} {h.unit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
